import React, { useState, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import DeviceTable from "./DeviceTable";
import AddDevicePopup from "./AddDeviceModal";
import DeleteDeviceModal from "./DeleteDeviceModal";
import "./DeviceSettings.css";
import { useDevicesWithMutations } from "../hooks/useDevicesWithMutations";
import { processDevicesForTable } from "../adapters/deviceTableAdapter";
import { TableSkeleton } from "../../../components/Common/Table";
import Button from "../../../components/Common/Button";
import { deviceTableColumns, DEVICES_PAGE_TITLE } from "../dataFiles/devicesPageDataFile";

const SKELETON_TAB_LABELS = ["All", "Production", "Consumption", "Storage"];
const skeletonTabStyle = {
  background: "none",
  border: "none",
  borderBottom: "3px solid transparent",
  padding: "10px 20px",
  fontSize: "15px",
  fontWeight: 400,
  color: "#94a3b8",
  cursor: "default",
};

const DevicesPage = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceToDelete, setDeviceToDelete] = useState(null);

  const storageAccount = sessionStorage.getItem("storageAccountName") || "";
  const microgridName = sessionStorage.getItem("microgridDesign") || "";
  const datasetName = microgridName ? `${microgridName}-sysconfig.json` : null;

  const {
    sysconfig,
    loading: devicesLoading,
    error: devicesError,
    reload,
    addDevice,
    updateDevice,
    deleteDevice,
  } = useDevicesWithMutations({
    storageAccount,
    microgridName,
    datasetName,
  });

  const loading = devicesLoading;
  const error = devicesError
    ? "Something went wrong while fetching device data."
    : "";

  const {
    storage: storageDevices,
    consumption: consumptionDevices,
    production: productionDevices,
  } = useMemo(() => processDevicesForTable(sysconfig), [sysconfig]);

  const allDevices = useMemo(() => {
    const combined = [
      ...productionDevices.map((d) => ({ ...d, deviceCategory: "Production" })),
      ...consumptionDevices.map((d) => ({ ...d, deviceCategory: "Consumption" })),
      ...storageDevices.map((d) => ({ ...d, deviceCategory: "Storage" })),
    ];
    return combined.sort((a, b) =>
      (a.device_name || "").localeCompare(b.device_name || ""),
    );
  }, [productionDevices, consumptionDevices, storageDevices]);

  const filteredDevices = useMemo(() => {
    if (activeTab === "All") return allDevices;
    return allDevices.filter((d) => d.deviceCategory === activeTab);
  }, [activeTab, allDevices]);

  const deviceTabs = [
    { key: "All", label: "All", count: allDevices.length },
    { key: "Production", label: "Production", count: productionDevices.length },
    { key: "Consumption", label: "Consumption", count: consumptionDevices.length },
    { key: "Storage", label: "Storage", count: storageDevices.length },
  ];

  const subHeader = microgridName
    ? `${microgridName} \u00b7 ${allDevices.length} ${allDevices.length === 1 ? "device" : "devices"}`
    : "";

  const handleAddDevice = () => {
    setSelectedDevice(null);
    setIsAddOpen(true);
  };

  const handleEdit = (device) => {
    setSelectedDevice(device);
    setIsAddOpen(true);
  };

  const handleDeleteClick = (device) => {
    setDeviceToDelete(device);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = useCallback(async () => {
    if (!deviceToDelete) return;

    const deviceId = deviceToDelete.device_id;
    if (!deviceId) {
      toast.error("This device was modified by another user. Please refresh.");
      setIsDeleteOpen(false);
      setDeviceToDelete(null);
      return;
    }

    setIsDeleting(true);
    try {
      await deleteDevice(deviceId);
      toast.success("Device deleted successfully!");
    } catch (err) {
      console.error("Error deleting device:", err);
      toast.error("Failed to delete device.");
    } finally {
      setIsDeleting(false);
    }

    setDeviceToDelete(null);
    setIsDeleteOpen(false);
  }, [deviceToDelete, deleteDevice]);

  const handleCloseAdd = () => {
    setIsAddOpen(false);
    setSelectedDevice(null);
  };

  const handleCloseDelete = () => {
    setIsDeleteOpen(false);
    setDeviceToDelete(null);
  };

  return (
    <div className={`main devices ${isAddOpen || isDeleteOpen ? "blurred" : ""}`}>
      <div className="container-fluid">
        {loading ? (
            <TableSkeleton
              columns={deviceTableColumns}
              rows={4}
              toolbar={{
                left: (
                  <div>
                    <h2 className="wg-page-header">{DEVICES_PAGE_TITLE}</h2>
                    {microgridName && (
                      <p style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 0 0" }}>
                        {microgridName}
                      </p>
                    )}
                  </div>
                ),
              }}
              header={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderBottom: "1px solid #e2e8f0", marginBottom: "16px" }}>
                  <div style={{ display: "flex", gap: "0" }}>
                    {SKELETON_TAB_LABELS.map((label, i) => (
                      <span
                        key={label}
                        style={{
                          ...skeletonTabStyle,
                          ...(i === 0
                            ? { borderBottom: "3px solid #3b82f6", fontWeight: 600, color: "#1e293b" }
                            : {}),
                        }}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                  <div style={{ paddingBottom: "8px" }}>
                    <Button onClick={() => {}}>+ Add Device</Button>
                  </div>
                </div>
              }
            />
          ) : error ? (
            <div className="alert alert-danger mt-4">{error}</div>
          ) : (
            <DeviceTable
              devices={filteredDevices}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onAddDevice={handleAddDevice}
              tabs={deviceTabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              subHeader={subHeader}
            />
          )}

        <DeleteDeviceModal
          open={isDeleteOpen}
          device={deviceToDelete}
          onConfirm={handleConfirmDelete}
          onClose={handleCloseDelete}
          loading={isDeleting}
        />

        {isAddOpen && (
          <AddDevicePopup
            activeTab={activeTab}
            onClose={handleCloseAdd}
            onSave={async () => {
              await new Promise((resolve) => setTimeout(resolve, 2000));
              await reload();
              setIsAddOpen(false);
            }}
            device={selectedDevice}
            addDevice={addDevice}
            updateDevice={updateDevice}
            sysconfig={sysconfig}
            storageAccount={storageAccount}
            microgridName={microgridName}
            datasetName={datasetName}
          />
        )}

        {(isAddOpen || isDeleteOpen) && <div className="overlay" />}
      </div>
    </div>
  );
};

export default DevicesPage;
